import { useSyncExternalStore } from 'react';
import { getAnonId } from '@/api/anon';
import { routes } from '@/api/routes';
import { claimPayload, hasUnclaimedProgress, markClaimed } from '@/save/saveStore';

// Мета-профиль (зеркало Contracts.Dtos.MeResponse)
export interface MeDto {
  id: string;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  locale: string;
  isAdmin: boolean;
  createdAt: string;
  entitlements: { fullGame: boolean };
  identities: { provider: string; linkedAt: string }[];
  consent: { accepted: boolean; version: string | null; currentVersion: string; needsReconsent: boolean };
}

interface RefreshDto { accessToken: string; expiresInSec: number; tokenType: string }

export type AuthStatus = 'loading' | 'authed' | 'guest';
export interface AuthState { status: AuthStatus; me: MeDto | null }

const CSRF_COOKIE = 'dg_csrf';
const CSRF_PATH = '/api/v1/auth';

/** Запас к expiresAt: запрос с токеном на грани истечения — сначала refresh. */
const EXPIRY_SKEW_MS = 30_000;

/** double-submit: клиент сам генерит токен, кладёт в cookie и шлёт тем же заголовком. */
function ensureCsrf(): string {
  const match = document.cookie.match(/(?:^|;\s*)dg_csrf=([^;]+)/);
  if (match) return match[1];
  const token = crypto.randomUUID().replace(/-/g, '');
  document.cookie = `${CSRF_COOKIE}=${token}; path=${CSRF_PATH}; secure; samesite=lax`;
  return token;
}

export function createAuthStore() {
  let state: AuthState = { status: 'loading', me: null };
  let accessToken: string | null = null;
  let expiresAt = 0; // мс-эпоха истечения access-токена (из expiresInSec)
  let refreshing: Promise<boolean> | null = null; // single-flight refresh
  const subs = new Set<() => void>();

  const set = (next: AuthState) => {
    state = next;
    subs.forEach((f) => f());
  };

  /** Тихий refresh по dg_rt-cookie; параллельные вызовы делят одну промису.
   *  Провал → сброс в guest (как при неудачном bootstrap). */
  function refresh(): Promise<boolean> {
    refreshing ??= (async () => {
      try {
        const csrf = ensureCsrf();
        const res = await fetch(routes.authRefresh, {
          method: 'POST',
          credentials: 'include',
          headers: new Headers({ 'X-CSRF-Token': csrf, 'X-Anon-Id': getAnonId() }),
        });
        if (!res.ok) throw new Error(`refresh ${res.status}`);
        const dto = (await res.json()) as RefreshDto;
        accessToken = dto.accessToken;
        expiresAt = Date.now() + dto.expiresInSec * 1000;
        return true;
      } catch {
        accessToken = null;
        set({ status: 'guest', me: null });
        return false;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  function withBearer(init?: RequestInit): RequestInit {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${accessToken}`);
    return { ...init, headers };
  }

  async function fetchMe(): Promise<MeDto> {
    const headers = new Headers({ Authorization: `Bearer ${accessToken}` });
    const res = await fetch(routes.me, { credentials: 'include', headers });
    if (!res.ok) throw new Error(`me ${res.status}`);
    return (await res.json()) as MeDto;
  }

  return {
    get: () => state,
    subscribe(f: () => void) { subs.add(f); return () => subs.delete(f); },
    get accessToken() { return accessToken; },
    refresh,

    /** fetch с Bearer: протухший по expiresAt токен обновляется ДО запроса;
     *  на 401 — ровно один refresh + повтор, повторный 401 отдаём как есть.
     *  Не-authed → обычный fetch без Authorization. */
    async authorizedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      if (state.status !== 'authed' || !accessToken) return fetch(input, init);
      if (Date.now() >= expiresAt - EXPIRY_SKEW_MS && !(await refresh())) return fetch(input, init);
      const res = await fetch(input, withBearer(init));
      if (res.status !== 401) return res;
      // токен мог протухнуть между проверкой и запросом (или отозван) — одна попытка
      if (!(await refresh())) return res;
      return fetch(input, withBearer(init));
    },

    /** Старт SPA: тихий refresh по dg_rt-cookie → access-токен → /me. Нет сессии → гость. */
    async bootstrap(): Promise<void> {
      if (!(await refresh())) return; // провал → guest внутри refresh()
      try {
        set({ status: 'authed', me: await fetchMe() });
      } catch {
        set({ status: 'guest', me: null });
        return;
      }

      // клейм гостевого прогресса — один раз после входа (§5.2, идемпотентен)
      if (hasUnclaimedProgress()) {
        try {
          const claim = await fetch(routes.meProgressClaim, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(claimPayload()),
          });
          if (claim.ok) markClaimed();
        } catch { /* ретрай при следующем bootstrap */ }
      }
    },

    /** URL авторизации провайдера (mode=json ставит dg_oauth-cookie этим же запросом). */
    async getAuthorizeUrl(provider: string, returnPath: string): Promise<string> {
      const url = `${routes.authStart(provider)}?return=${encodeURIComponent(returnPath)}&mode=json`;
      const res = await fetch(url, {
        credentials: 'include',
        headers: new Headers({ 'X-Anon-Id': getAnonId() }),
      });
      if (!res.ok) throw new Error(`start ${res.status}`);
      const body = (await res.json()) as { authorizeUrl: string };
      return body.authorizeUrl;
    },

    async logout(): Promise<void> {
      try {
        await fetch(routes.authLogout, { method: 'POST', credentials: 'include' });
      } finally {
        accessToken = null;
        expiresAt = 0;
        set({ status: 'guest', me: null });
      }
    },

    /** PATCH /me (displayName / settings-мёрж в player_state) — только залогиненному.
     *  Ответ = свежий MeResponse, кладём его в стейт. Ошибка — наружу (экран покажет). */
    async patchMe(body: { displayName?: string; settings?: Record<string, unknown> }): Promise<void> {
      if (state.status !== 'authed' || !accessToken) return;
      const res = await fetch(routes.me, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`me patch ${res.status}`);
      set({ status: 'authed', me: (await res.json()) as MeDto });
    },
  };
}

export type AuthStore = ReturnType<typeof createAuthStore>;

export const authStore = typeof window !== 'undefined' ? createAuthStore() : null!;

/** Модульный шорткат к authStore.authorizedFetch — для API-вызовов вне React. */
export function authorizedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return authStore.authorizedFetch(input, init);
}

export function useAuth(): AuthState {
  return useSyncExternalStore(authStore.subscribe, authStore.get, authStore.get);
}
